const STORAGE_KEY = "eusebionews-notifications-enabled";
const LAST_SEEN_KEY = "eusebionews-last-seen-article";
const DISMISS_KEY = "eusebionews-notification-prompt-dismissed-at";

const PROMPT_REASK_MS = 7 * 24 * 60 * 60 * 1000;

export function isNotificationSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

export function getNotificationPermission(): NotificationPermission | "unsupported" {
  if (!isNotificationSupported()) return "unsupported";
  return Notification.permission;
}

export function getStoredNotificationPreference(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export function setStoredNotificationPreference(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? "true" : "false");
  } catch {
    // Ignore storage failures.
  }
}

export function markNotificationPromptDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // Ignore storage failures.
  }
}

export function clearNotificationPromptDismissed(): void {
  try {
    localStorage.removeItem(DISMISS_KEY);
  } catch {
    // Ignore storage failures.
  }
}

/** True se já existe registro de dismiss (mesmo após a janela de 7 dias). */
export function hasNotificationPromptDismissRecord(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) != null;
  } catch {
    return false;
  }
}

/** True enquanto o usuário pediu para não ver o prompt (janela de 7 dias). */
export function isNotificationPromptSuppressed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const dismissedAt = Number(raw);
    if (!Number.isFinite(dismissedAt)) return false;
    return Date.now() - dismissedAt < PROMPT_REASK_MS;
  } catch {
    return false;
  }
}

/** Retorna true se o prompt pode aparecer (nunca dispensou ou já passaram 7 dias). */
export function shouldShowNotificationPromptAgain(): boolean {
  return !isNotificationPromptSuppressed();
}

/**
 * Alinha preferência local com a permissão real do browser.
 * STORAGE_KEY só reflete grant real — evita falso "revogado" a cada refresh.
 */
export function reconcileNotificationPreference(): {
  permission: NotificationPermission | "unsupported";
  enabled: boolean;
  wasRevoked: boolean;
} {
  const permission = getNotificationPermission();
  if (permission === "unsupported") {
    setStoredNotificationPreference(false);
    return { permission, enabled: false, wasRevoked: false };
  }

  const stored = getStoredNotificationPreference();
  const enabled = permission === "granted";
  // Revogação real: tínhamos grant persistido e o browser tirou o "granted"
  const wasRevoked = stored && !enabled;

  if (stored !== enabled) {
    setStoredNotificationPreference(enabled);
  }
  if (wasRevoked) {
    clearNotificationPromptDismissed();
  }

  return { permission, enabled, wasRevoked };
}

export async function requestNotificationPermission(): Promise<NotificationPermission | "unsupported"> {
  if (!isNotificationSupported()) return "unsupported";
  if (Notification.permission === "granted") {
    setStoredNotificationPreference(true);
    return "granted";
  }
  if (Notification.permission === "denied") {
    setStoredNotificationPreference(false);
    return "denied";
  }
  const result = await Notification.requestPermission();
  setStoredNotificationPreference(result === "granted");
  return result;
}

export function getLastSeenArticleId(): number | null {
  try {
    const raw = localStorage.getItem(LAST_SEEN_KEY);
    if (!raw) return null;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
}

export function setLastSeenArticleId(id: number): void {
  try {
    localStorage.setItem(LAST_SEEN_KEY, String(id));
  } catch {
    // Ignore storage failures.
  }
}

function canNotify(): boolean {
  return isNotificationSupported() && Notification.permission === "granted";
}

export function showWelcomeNotification(): void {
  if (!canNotify()) return;
  try {
    new Notification("Eusébio News", {
      body: "Você receberá alertas quando publicarmos novas matérias.",
      icon: "/logo-eusebio-news-88.png",
      tag: "eusebionews-welcome",
    });
  } catch {
    // Some browsers block programmatic notifications.
  }
}

export function showNewsNotification(article: { id: number; title: string; slug: string; summary?: string }): void {
  if (!canNotify()) return;
  try {
    const notification = new Notification(article.title, {
      body: article.summary?.slice(0, 140) || "Nova matéria publicada no Eusébio News.",
      icon: "/logo-eusebio-news-88.png",
      tag: `eusebionews-article-${article.id}`,
      data: { url: `/materia/${article.slug}` },
    });
    notification.onclick = () => {
      window.focus();
      window.location.assign(`/materia/${article.slug}`);
      notification.close();
    };
  } catch {
    // Some browsers block programmatic notifications.
  }
}
